export default {
    title: 'Accident Intake',
    heading: 'Accident Intake',
    menu_label: 'Intake',
    submit_success: 'Intake submitted successfully.',
    submit_error: 'Unable to submit intake. Check the Network tab for the request payload.',
    success: {
        heading: 'Intake submitted',
        message:
            "Saved to MedMatch's intake database. Copy the summary below into the chart, an email, or an LLM prompt to draft the H&P — this is the same structured data, just rendered readable for now.",
        copy: 'Copy summary to clipboard',
        copied: 'Copied',
        download: 'Download as PDF',
        done: 'Done',
    },

    breadcrumbs: {
        intake: 'Intake',
        about_you: 'About You',
        what_happened: 'What Happened',
        how_you_felt: 'How You Felt Afterward',
        symptoms_today: 'Your Symptoms Today',
        symptom_details: 'Symptom Details',
        health_screening: 'Health Screening',
        treatment_received: "Treatment You've Received",
        treating_providers: 'Treating Providers',
        tests_imaging: 'Tests & Imaging',
        previous_injuries: 'Previous Injuries',
        medical_surgical: 'Medical & Surgical History',
        medications_allergies: 'Medications & Allergies',
        work_information: 'Work Information',
        social_family: 'Social & Family History',
        daily_activities: 'Daily Activities',
        health_questions: 'Health Questions',
        review_submit: 'Review & Submit',
        confirm_submit: 'Confirm & Submit',
    },

    step1: {
        progress: 'Step {current} of {total} — About You',
        heading: 'About You',
        description: 'A few quick questions before we get into your injury.',
    },

    step2: {
        progress: 'Step {current} of {total} — What Happened',
        heading: 'What Happened',
        description: 'Tell us about the incident that caused the injuries you’re being seen for.',
    },

    step3: {
        progress: 'Step {current} of {total} — How You Felt Afterward',
        heading: 'How You Felt Afterward',
        description: 'Symptoms right after the incident, and the care you received.',
    },

    step4: {
        progress: 'Step {current} of {total} — Your Symptoms Today',
        heading: 'Your Symptoms Today',
        description:
            'List every problem you’re having because of this incident. Add the one bothering you most first — the order below is used as priority.',
    },

    step5: {
        progress: 'Step {current} of {total} — Symptom Details',
        heading: 'Symptom Details',
        description: 'A few more details on each problem you listed.',
    },

    step6: {
        progress: 'Step {current} of {total} — Health Screening',
        heading: 'Health Screening Questions',
        description: 'Since this injury, have you experienced any of the following?',
    },

    step7: {
        progress: "Step {current} of {total} — Treatment You've Received",
        heading: "Treatment You've Received",
        description:
            "Since the incident, list every treatment you've had for these injuries — ER visits, PT, chiropractic, injections, surgery, medications, etc.",
    },

    step8: {
        progress: 'Step {current} of {total} — Treating Providers',
        heading: 'Treating Providers',
        description: 'Which doctors or healthcare professionals have treated you for injuries from this incident?',
    },

    step9: {
        progress: 'Step {current} of {total} — Tests & Imaging',
        heading: 'Tests & Imaging',
        description: 'What tests or imaging have you had because of this incident?',
    },

    step10: {
        progress: 'Step {current} of {total} — Previous Injuries',
        heading: 'Previous Injuries',
        description: 'Before this incident, had you ever had any of the following?',
    },

    step11: {
        progress: 'Step {current} of {total} — Medical & Surgical History',
        heading: 'Medical & Surgical History',
        description: '',
    },

    step12: {
        progress: 'Step {current} of {total} — Medications & Allergies',
        heading: 'Medications & Allergies',
        description: '',
    },

    step13: {
        progress: 'Step {current} of {total} — Work Information',
        heading: 'Work Information',
        description: '',
    },

    step14: {
        progress: 'Step {current} of {total} — Social & Family History',
        heading: 'Social & Family History',
        description: '',
    },

    step15: {
        progress: 'Step {current} of {total} — Daily Activities',
        heading: 'Daily Activities',
        description: 'Compared with before the incident, how are these activities affected?',
    },

    step16: {
        progress: 'Step {current} of {total} — Health Questions',
        heading: 'Health Questions',
        description:
            'A general review of your overall health. Items already reported as symptoms are pre-marked — confirm or change as needed.',
    },

    step17: {
        progress: 'Step {current} of {total} — Review & Submit',
        heading: 'Review & Submit',
        description: 'Please check everything below before submitting.',
    },

    step18: {
        progress: 'Step {current} of {total} — Confirm & Submit',
        heading: 'Confirm & Submit',
        description: '',
    },

    general: {
        step1: {
            progress: 'Step {current} of {total} · About You',
            heading: 'About You',
            description: 'A few quick questions before we get into your visit.',
        },
        step2: {
            progress: 'Step {current} of {total} · Reason for Visit',
            heading: 'Reason for Visit',
            description:
                "List everything you'd like to discuss at this visit. Add the one that matters most first — the order below is used as priority.",
        },
        step3: {
            progress: 'Step {current} of {total} · Health Screening',
            heading: 'Health Screening Questions',
            description: 'Have you recently experienced any of the following?',
        },
        step4: {
            progress: "Step {current} of {total} · Treatment You've Received",
            heading: "Treatment You've Received",
            description:
                "List any treatments or ongoing care you're currently receiving — specialist visits, PT, injections, surgery, medications, etc.",
        },
        step5: {
            progress: 'Step {current} of {total} · Treating Providers',
            heading: 'Treating Providers',
            description: 'Which doctors or healthcare professionals are currently treating you?',
        },
        step6: {
            progress: 'Step {current} of {total} · Tests & Imaging',
            heading: 'Tests & Imaging',
            description: 'What tests or imaging have you had recently?',
        },
        step7: {
            progress: 'Step {current} of {total} · Medical & Surgical History',
            heading: 'Medical & Surgical History',
            description: '',
        },
        step8: {
            progress: 'Step {current} of {total} · Medications & Allergies',
            heading: 'Medications & Allergies',
            description: '',
        },
        step9: {
            progress: 'Step {current} of {total} · Social & Family History',
            heading: 'Social & Family History',
            description: '',
        },
        step10: {
            progress: 'Step {current} of {total} · Daily Activities',
            heading: 'Daily Activities',
            description: 'Compared with your usual baseline, how are these activities affected?',
        },
        step11: {
            progress: 'Step {current} of {total} · Preventive Care',
            heading: 'Preventive Care',
            description: 'A few questions about routine health maintenance.',
        },
        step12: {
            progress: 'Step {current} of {total} · Health Questions',
            heading: 'Health Questions',
            description: 'A general review of your overall health.',
        },
        step13: {
            progress: 'Step {current} of {total} · Review & Submit',
            heading: 'Review & Submit',
            description: 'Please check everything below before submitting.',
        },
        step14: {
            progress: 'Step {current} of {total} · Confirm & Submit',
            heading: 'Confirm & Submit',
            description: '',
        },
    },
}
